import React, { createContext, useContext, useRef, useState } from 'react'
import { View, Text, Animated } from 'react-native'

interface Toast {
  id: number
  message: string
  type: 'success' | 'error' | 'info'
}

interface ToastContextValue {
  showToast: (message: string, type?: Toast['type']) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  function showToast(message: string, type: Toast['type'] = 'info') {
    const id = ++idRef.current
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000)
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <View className="absolute bottom-12 left-4 right-4 gap-2 z-50">
        {toasts.map((toast) => (
          <View
            key={toast.id}
            className={`rounded-xl px-4 py-3 ${
              toast.type === 'error'
                ? 'bg-blood'
                : toast.type === 'success'
                  ? 'bg-green-700'
                  : 'bg-slate-700'
            }`}
          >
            <Text className="text-white text-sm font-medium font-typed">{toast.message}</Text>
          </View>
        ))}
      </View>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
